import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Users, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/app/Layout";
import { useCachedGet } from "@/lib/cache";
import { STAGE_LABELS, stageLabel } from "@/lib/stages";

const PAGE = 50;
const TH = "px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap text-left";
const TD = "px-4 py-3 align-middle";
const CHIP = { screening: "tp-chip-indigo", video: "tp-chip-indigo", coding: "tp-chip-indigo", scorecard: "tp-chip-indigo", incomplete: "tp-chip-amber", shortlist: "tp-chip-emerald", rejected: "tp-chip-rose" };
const SEL = "text-sm px-3 py-2 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";
const SORTS = {
  name: (c) => (c.name || "").toLowerCase(), job: (c) => (c.job_title || "").toLowerCase(), stage: (c) => c.stage || "",
  score: (c) => c.overall_score ?? -1, added: (c) => c.created_at || "",
};
const TEXT_SORTS = ["name", "job", "stage"];
const score = (v) => (v == null ? <span className="text-slate-300">—</span> : Math.round(v));

function Head({ k, label, sort, setSort, right }) {
  const on = sort.k === k, Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH} ${right ? "text-right" : ""}`}>
      <button data-testid={`cand-sort-${k}`} onClick={() => setSort((s) => ({ k, dir: s.k === k ? (s.dir === "asc" ? "desc" : "asc") : TEXT_SORTS.includes(k) ? "asc" : "desc" }))}
        className={`inline-flex items-center gap-1 uppercase tracking-wider ${on ? "text-indigo-600" : "hover:text-slate-800"}`}>{label}<Icon className={`w-3 h-3 ${on ? "" : "opacity-40"}`} /></button>
    </th>
  );
}

export default function CandidatesPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data] = useCachedGet("/candidates");
  const [q, setQ] = useState("");
  const [stage, setStage] = useState(params.get("stage") || "");
  const [job, setJob] = useState("");
  const [client, setClient] = useState("");
  const [sort, setSort] = useState({ k: "added", dir: "desc" });
  const [page, setPage] = useState(0);
  const all = data || [];

  const jobs = useMemo(() => [...new Map(all.map((c) => [c.job_id, c.job_title])).entries()].sort((a, b) => (a[1] || "").localeCompare(b[1] || "")), [all]);
  const clients = useMemo(() => [...new Map(all.filter((c) => c.client_id).map((c) => [c.client_id, c.client])).entries()], [all]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const f = all.filter((c) => (!t || [c.name, c.email, c.phone, c.job_title].some((v) => (v || "").toLowerCase().includes(t)))
      && (!stage || (stage === "hired" ? c.hired : c.stage === stage)) && (!job || c.job_id === job) && (!client || c.client_id === client));
    const g = SORTS[sort.k], m = sort.dir === "asc" ? 1 : -1;
    return f.sort((a, b) => { const x = g(a), y = g(b); return x < y ? -m : x > y ? m : 0; });
  }, [all, q, stage, job, client, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const cur = Math.min(page, pages - 1);
  const shown = rows.slice(cur * PAGE, cur * PAGE + PAGE);
  const reset = (fn) => (e) => { fn(e.target.value); setPage(0); };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">People</div>
        <h1 className="font-display text-3xl font-bold">All Candidates</h1>
        <p className="text-sm text-slate-500 mt-1">Every candidate across the jobs you can access.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cand-filters">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="cand-search" value={q} onChange={reset(setQ)} placeholder="Search name, email, phone or job…" className={`${SEL} w-full pl-9`} />
        </div>
        <select data-testid="cand-filter-stage" value={stage} onChange={reset(setStage)} className={SEL}>
          <option value="">All stages</option>
          {Object.keys(STAGE_LABELS).map((k) => <option key={k} value={k}>{stageLabel(k)}</option>)}
          <option value="hired">Hired</option>
        </select>
        <select data-testid="cand-filter-job" value={job} onChange={reset(setJob)} className={`${SEL} max-w-[220px]`}>
          <option value="">All jobs</option>{jobs.map(([id, t]) => <option key={id} value={id}>{t}</option>)}
        </select>
        {clients.length > 0 && (
          <select data-testid="cand-filter-client" value={client} onChange={reset(setClient)} className={`${SEL} max-w-[200px]`}>
            <option value="">All clients</option>{clients.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        )}
        <span className="text-xs text-slate-500 ml-auto" data-testid="cand-count">{data ? `${rows.length} of ${all.length} candidates` : ""}</span>
      </div>

      {data === undefined ? <div className="text-sm text-slate-500">Loading…</div>
        : rows.length === 0 ? (
          <div className="tp-card p-10 text-center text-sm text-slate-500" data-testid="cand-empty"><Users className="w-6 h-6 mx-auto mb-2 text-slate-400" />No candidates match.</div>
        ) : (
          <div className="tp-card overflow-x-auto" data-testid="cand-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-50 border-b border-[color:var(--bd-sub)]">
                <tr>
                  <Head k="name" label="Candidate" sort={sort} setSort={setSort} />
                  <Head k="job" label="Job" sort={sort} setSort={setSort} />
                  <Head k="stage" label="Stage" sort={sort} setSort={setSort} />
                  <th className={`${TH} text-right`}>Phone</th><th className={`${TH} text-right`}>Video</th><th className={`${TH} text-right`}>Coding</th>
                  <Head k="score" label="Overall" sort={sort} setSort={setSort} right />
                  <Head k="added" label="Added" sort={sort} setSort={setSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--bd-sub)]">
                {shown.map((c) => (
                  <tr key={c.id} data-testid={`cand-row-${c.id}`} onClick={() => nav(`/app/candidates/${c.id}`)} className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <td className={`${TD} max-w-[260px]`}>
                      <div className="font-medium text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.email}</div>
                    </td>
                    <td className={`${TD} max-w-[260px]`}>
                      <div className="text-slate-800 truncate">{c.job_title}</div>
                      {c.client && <div className="text-xs text-slate-500 truncate">{c.client}</div>}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      <span className={`tp-chip ${CHIP[c.stage] || ""}`}>{stageLabel(c.stage)}</span>
                      {c.hired && <span className="tp-chip tp-chip-emerald ml-1">Hired</span>}
                    </td>
                    <td className={`${TD} text-right`}>{score(c.voice_score)}</td>
                    <td className={`${TD} text-right`}>{score(c.video_score)}</td>
                    <td className={`${TD} text-right`}>{score(c.coding_score)}</td>
                    <td className={`${TD} text-right font-semibold text-slate-900`}>{score(c.overall_score)}</td>
                    <td className={`${TD} text-slate-500 whitespace-nowrap`}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm" data-testid="cand-pager">
          <button data-testid="cand-prev" disabled={cur === 0} onClick={() => setPage(cur - 1)} className="p-1.5 rounded border border-[color:var(--bd-sub)] disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-slate-600">Page {cur + 1} of {pages}</span>
          <button data-testid="cand-next" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} className="p-1.5 rounded border border-[color:var(--bd-sub)] disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </Layout>
  );
}
